import { Fragment, useEffect, useState, type ReactNode } from "react";
import { Link, useLocation } from "react-router";
import { PLAN_PERKS } from "../../shared/market";
import { PANEL, sellerPath, type PanelPlace, type SellerSection } from "../../shared/sellerPaths";
import { useAuth } from "../auth/AuthProvider";
import { useI18n } from "../i18n/I18nProvider";
import type { MessageKey } from "../i18n/messages";
import { imageUrl } from "../lib/api";
import type { MySeller } from "./api";
import { stopActing } from "../lib/actAs";
import { CountBadge, useInbox } from "./InboxProvider";
import { PanelBell, PanelSearch } from "./PanelTools";
import { GameSwitcher } from "../components/GameSwitcher";

/** A menu entry: the section it opens and the ones that light it up (Ventas is also its offers and orders). */
interface Entry {
  key: string;
  to: SellerSection;
  icon: string;
  label: MessageKey;
  covers: readonly SellerSection[];
  badge?: number;
}

/** What the admin works on from inside a shop it set up (sales, customers and the plan are the owner's). */
export const ACTING_ENTRIES = ["inicio", "publicaciones", "tienda", "demanda", "estadisticas", "ajustes"];

const isOn = (e: Entry, place: PanelPlace) => (place && "order" in place ? e.key === "ventas" : place ? e.covers.includes(place.section) : false);

/**
 * Mi tienda's own frame, like the admin's: no catalog search, cart or footer. Wide screens get a side
 * menu with every section in sight (the shop's work; how it grows; its plan and settings); phones a bar
 * at the bottom (Inicio · Publicaciones · Ventas · Más, the rest in a sheet) and a "+ Publicar" button.
 */
export function SellerShell({
  seller,
  place,
  limited = false,
  closed = false,
  clientes = "clientes",
  acting = false,
  children,
}: {
  seller: MySeller;
  place: PanelPlace;
  /** Under review: only what it can use. */
  limited?: boolean;
  /** Paused or banned: no menu, only why. */
  closed?: boolean;
  /** Where Clientes opens (a plan with coupons but no customer list: its coupons). */
  clientes?: SellerSection;
  /** The admin working from inside a shop it set up: a bar that says so, and only ACTING_ENTRIES. */
  acting?: boolean;
  children: ReactNode;
}) {
  const { t } = useI18n();
  const { inbox } = useInbox();
  const auth = useAuth();
  const { pathname } = useLocation();
  const [more, setMore] = useState(false);

  const all: Entry[] = closed
    ? []
    : limited
      ? [
          {
            key: "inicio",
            to: "inicio",
            icon: "🏠",
            label: "mkt.tab.inicio",
            covers: ["inicio"],
          },
          {
            key: "publicaciones",
            to: "publicaciones",
            icon: "🃏",
            label: "mkt.tab.publicaciones",
            covers: ["publicaciones", "publicar"],
          },
          {
            key: "ajustes",
            to: "ajustes",
            icon: "⚙️",
            label: "mkt.tab.ajustes",
            covers: ["ajustes"],
          },
        ]
      : [
          {
            key: "inicio",
            to: "inicio",
            icon: "🏠",
            label: "mkt.tab.inicio",
            covers: ["inicio"],
          },
          {
            key: "publicaciones",
            to: "publicaciones",
            icon: "🃏",
            label: "mkt.tab.publicaciones",
            covers: ["publicaciones", "publicar"],
          },
          {
            key: "ventas",
            to: "pedidos",
            icon: "💰",
            label: "mkt.tab.ventas",
            covers: ["pedidos", "ofertas"],
            badge: inbox.sales + inbox.sellerOffers,
          },
          {
            key: "clientes",
            to: clientes,
            icon: "👥",
            label: "mkt.tab.clientes",
            covers: ["clientes", "cupones", "emails"],
          },
          {
            key: "tienda",
            to: "tienda",
            icon: "🎨",
            label: "mkt.tab.tienda",
            covers: ["tienda"],
          },
        ];
  const allExtra: Entry[] =
    limited || closed
      ? []
      : [
          {
            key: "demanda",
            to: "demanda",
            icon: "🔎",
            label: "mkt.tab.demanda",
            covers: ["demanda"],
          },
          {
            key: "estadisticas",
            to: "estadisticas",
            icon: "📈",
            label: "mkt.tab.estadisticas",
            covers: ["estadisticas", "est_cartas", "est_precios"],
          },
          {
            key: "plan",
            to: "plan",
            icon: "⭐",
            label: "mkt.tab.plan",
            covers: ["plan"],
          },
          {
            key: "ajustes",
            to: "ajustes",
            icon: "⚙️",
            label: "mkt.tab.ajustes",
            covers: ["ajustes"],
          },
          // The API is VMAX's (as the panel's own guard): other plans don't get the entry.
          ...(PLAN_PERKS[seller.plan?.name ?? "basic"].api
            ? [
                {
                  key: "integraciones",
                  to: "integraciones",
                  icon: "🔌",
                  label: "mkt.tab.integraciones",
                  covers: ["integraciones"],
                } satisfies Entry,
              ]
            : []),
        ];
  // Leaving the panel while working as a shop: back to being the admin.
  const leave = acting ? () => stopActing() : undefined;
  const main = acting ? all.filter((e) => ACTING_ENTRIES.includes(e.key)) : all;
  const extra = acting ? allExtra.filter((e) => ACTING_ENTRIES.includes(e.key)) : allExtra;
  useEffect(() => setMore(false), [pathname]);
  useEffect(() => {
    if (!more) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setMore(false);
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [more]);

  const item = (e: Entry, onClick?: () => void) => (
    <Link key={e.key} to={sellerPath(e.to)} className={isOn(e, place) ? "sp-link is-current" : "sp-link"} aria-current={isOn(e, place) ? "page" : undefined} onClick={onClick}>
      <span className="sp-icon" aria-hidden="true">
        {e.icon}
      </span>
      <span className="sp-label">{t(e.label)}</span>
      {e.badge ? <CountBadge count={e.badge} /> : null}
    </Link>
  );
  const shopUrl = `/vendedores/${seller.handle}`;
  const logo = seller.shop?.logo ?? null;
  const bottom = main.filter((e) => ["inicio", "publicaciones", "ventas"].includes(e.key));
  const rest = [...main.filter((e) => !bottom.includes(e)), ...extra];

  return (
    <div className="sp">
      {acting && (
        <div className="sp-acting" role="status">
          <span>🛠️ {t("sp.acting", { handle: seller.handle })}</span>
          <button
            type="button"
            className="btn btn-outline btn-sm"
            onClick={() => {
              stopActing();
              window.location.assign("/admin/mercado");
            }}
          >
            {t("sp.actingExit")}
          </button>
        </div>
      )}
      <header className="sp-top">
        <Link to="/" className="sp-brand" title={t("sp.toSite")} onClick={leave}>
          <img src="/brand/logo-mark@2x.png" alt="pokefans.fun" width={28} height={30} />
        </Link>
        <Link to={PANEL} className="sp-shop">
          {logo ? (
            <img src={imageUrl(logo)} alt="" width={28} height={28} className="sp-avatar" />
          ) : (
            <span className="sp-avatar" aria-hidden="true">
              {seller.displayName.charAt(0).toUpperCase()}
            </span>
          )}
          <span className="sp-shop-name">{seller.displayName}</span>
        </Link>
        {!limited && !closed && <PanelSearch />}
        <div className="sp-top-actions">
          {/* Publishing, "Lo que buscan" and the sections' sets are of the game picked here, like on the site. */}
          <GameSwitcher className="game-switch-panel" />
          {!limited && !closed && <PanelBell />}
          {!(seller.managed && !seller.managed.active) && (
            <Link to={shopUrl} className="btn btn-link btn-sm sp-view">
              {t("home.seeShop")} ↗
            </Link>
          )}
          <Link to="/cuenta" className={closed ? "btn btn-link btn-sm" : "btn btn-link btn-sm sp-wide"} onClick={leave}>
            {t("auth.account")}
          </Link>
          <button type="button" className={closed ? "btn btn-link btn-sm" : "btn btn-link btn-sm sp-wide"} onClick={() => (stopActing(), void auth.signOut())}>
            {t("auth.logout")}
          </button>
        </div>
      </header>
      <div className={closed ? "sp-body sp-body-closed" : "sp-body"}>
        {!closed && (
          <nav className="sp-side" aria-label={t("mkt.myShop")}>
            {main.map((e) => item(e))}
            {extra.map((e) => (
              <Fragment key={e.key}>
                {(e.key === "demanda" || e.key === "plan") && <hr className="sp-sep" />}
                {item(e)}
              </Fragment>
            ))}
            <Link to={sellerPath("publicar")} className="btn btn-primary sp-side-publish">
              + {t("home.publish")}
            </Link>
          </nav>
        )}
        <main id="main" className="sp-main">
          {children}
        </main>
      </div>
      {!closed && (
        <nav className="sp-bottom" aria-label={t("mkt.myShop")}>
          {bottom.map((e) => item(e))}
          <button type="button" className={rest.some((e) => isOn(e, place)) ? "sp-link is-current" : "sp-link"} aria-expanded={more} aria-controls="sp-more" onClick={() => setMore(!more)}>
            <span className="sp-icon" aria-hidden="true">
              ☰
            </span>
            <span className="sp-label">{t("mkt.tab.mas")}</span>
          </button>
        </nav>
      )}
      {!closed && !(place && "section" in place && place.section === "publicar") && (
        <Link to={sellerPath("publicar")} className="sp-fab" aria-label={t("home.publish")}>
          <span aria-hidden="true">+</span>
        </Link>
      )}
      {more && (
        <div className="sp-sheet-backdrop" onMouseDown={(e) => e.target === e.currentTarget && setMore(false)}>
          <nav id="sp-more" className="sp-sheet" aria-label={t("mkt.tab.mas")}>
            {rest.map((e) => item(e, () => setMore(false)))}
            <Link to="/cuenta" className="sp-link" onClick={leave}>
              <span className="sp-icon" aria-hidden="true">
                👤
              </span>
              <span className="sp-label">{t("auth.account")}</span>
            </Link>
            <button type="button" className="sp-link" onClick={() => (stopActing(), void auth.signOut())}>
              <span className="sp-icon" aria-hidden="true">
                ↩
              </span>
              <span className="sp-label">{t("auth.logout")}</span>
            </button>
          </nav>
        </div>
      )}
    </div>
  );
}

/** The sub-tabs of a menu entry that holds several sections (Ventas: pedidos, ofertas). A pill's name is its section's, unless `names` says otherwise. */
export function SectionTabs({ sections, current, label, badge, names }: { sections: readonly SellerSection[]; current: SellerSection; label: string; badge?: (s: SellerSection) => number; names?: Partial<Record<SellerSection, MessageKey>> }) {
  const { t } = useI18n();
  return (
    <nav className="subtabs" aria-label={label}>
      {sections.map((s) => (
        <Link key={s} to={sellerPath(s)} className={s === current ? "subtab is-active" : "subtab"} aria-current={s === current ? "page" : undefined}>
          {t(names?.[s] ?? (`mkt.tab.${s}` as MessageKey))} <CountBadge count={badge?.(s) ?? 0} />
        </Link>
      ))}
    </nav>
  );
}
